import type { GetStaticProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Header from '../components/Header/Header';
import Connect from '../components/Connects/Connect';
import axios from 'axios';
import { OrganizationSchema, SoftwareApplicationSchema } from '../components/StructuredData';
import SEO from '../components/SEO';

// Lazy load heavy components for better code splitting
const Books = dynamic(() => import('../components/Books/Books'), {
  loading: () => <div style={{ minHeight: '400px' }} />,
});

const SimpleSection = dynamic(() => import('../components/SimpleSection/SimpleSection'), {
  loading: () => <div style={{ minHeight: '200px' }} />,
});

const VideoSection = dynamic(() => import('../components/VideoSection/VideoSection'), {
  loading: () => <div style={{ minHeight: '500px' }} />,
});

const BlogSection = dynamic(() => import('../components/BlogSection'), {
  loading: () => <div style={{ minHeight: '300px' }} />,
});

const Footer = dynamic(() => import('../components/Footer/Footer'));

interface HomePageProps {
  pageData: any;
  products: any[];
  aboutData: any[];
  blogPosts: any[];
}

const Home: NextPage<HomePageProps> = ({
  pageData,
  products,
  aboutData,
  blogPosts,
}) => {
  return (
    <div className="containerAnimationMain">
      <SEO
        title="Universal Video Plugin Framework | Cross-Platform"
        description="Universal video plugin framework supporting Windows, macOS, and Linux. Compatible with Adobe Premiere Pro, DaVinci Resolve, Final Cut Pro, Sony Vegas Pro, Nuke, Natron, OFX, OpenFX hosts. GPU-accelerated with CUDA, OpenCL, Metal, and DirectX technologies. Professional VFX and video editing solutions with 25+ years expertise."
        keywords="video plugin, adobe premiere, davinci resolve, video editing, plugin development"
      />
      <OrganizationSchema
        email={aboutData?.[0]?.email}
        phone={aboutData?.[0]?.phone}
        address={
          aboutData?.[0]?.address
            ? {
                streetAddress: aboutData[0].address,
              }
            : undefined
        }
      />
      <SoftwareApplicationSchema />
      <Header />

      <VideoSection data={pageData} />

      <Books products={products} />

      <SimpleSection
        data={{
          components: [
            {
              name: 'msg_section',
              mainHeading: 'Powering Creativity with Performance',
              subHeading:
                'Epen empowers creators with innovative video editing solutions, cross-platform technologies, and GPU-accelerated tools. Backed by specialists with over 25 years of experience, our flagship product streamlines complex editing tasks through advanced graphic programming and algorithm design—helping creators and businesses edit faster and smarter.',
            },
          ],
        }}
      />

      <Connect aboutData={aboutData} />

      {blogPosts && blogPosts.length > 0 && <BlogSection posts={blogPosts} />}

      <Footer aboutData={aboutData} />
    </div>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    const BASE_URL = process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1;

    // Get blog posts - kept as its own try/catch so a blog-posts failure
    // degrades gracefully instead of failing the whole page.
    const fetchBlogPosts = async () => {
      try {
        const blogResponse = await axios.get(
          `${BASE_URL}/blog-posts?status=published&limit=12&sort=-publishedAt`
        );
        return blogResponse.data.data || [];
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        return [];
      }
    };

    // These four requests are independent of each other, so run them
    // concurrently instead of one after another.
    const [homePageResponse, productsResponse, aboutData, blogPosts] = await Promise.all([
      axios.get(`${BASE_URL}/pages/home_page`),
      axios.get(`${BASE_URL}/products`),
      axios.get(`${BASE_URL}/about-us`),
      fetchBlogPosts(),
    ]);

    const productIDs = productsResponse.data.data.map((product: any) => product._id);

    // Same for the per-product lookups: each product's two calls, and every
    // product's pair, are all independent - fan them out together instead of
    // 2×N sequential round-trips.
    const trialAvailsEntries = await Promise.all(
      productIDs.map(async (id: string) => {
        const [readyToSell, hasFreeTrial] = await Promise.all([
          axios.get(`${BASE_URL}/product-details/ready-to-sell/${id}`),
          axios.get(`${BASE_URL}/product-details/has-free-trial/${id}`),
        ]);

        return [
          id,
          {
            hasFreeTrial: hasFreeTrial.data.data.hasFreeTrial,
            readyToSell: readyToSell.data.data.readyToSell,
          },
        ] as const;
      })
    );

    const trialAvails: {
      [key: string]: { hasFreeTrial: boolean; readyToSell: boolean };
    } = Object.fromEntries(trialAvailsEntries);

    return {
      props: {
        pageData: homePageResponse.data.data || null,
        products:
          productsResponse.data.data.map((product: any) => ({
            ...product,
            ...trialAvails[product._id],
          })) || [],
        aboutData: aboutData.data.data || [],
        blogPosts: blogPosts,
      },
      revalidate: 60,
    };
  } catch (error) {
    return {
      props: {
        pageData: null,
        products: [],
        aboutData: [],
        blogPosts: [],
      },
    };
  }
};

export default Home;
